import { format, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns';
import type { FormatDistanceToNowOptions, FormatDistanceToNowStrictOptions } from 'date-fns';
import { de, es, fr, it, ja, nl, pt, sv } from 'date-fns/locale';
import type { Locale } from 'date-fns/locale';
import i18next from 'i18next';

/**
 * Safe wrappers for parse-and-format operations.
 * Swallow errors silently, return fallback, let the component always render.
 * Users can see missing/empty data and correct it in the backend.
 */

/** Map i18n language codes to date-fns locale objects. English is the default (no import needed). */
const DATE_FNS_LOCALES: Record<string, Locale> = { de, es, fr, it, ja, nl, pt, sv };

/** Get the current date-fns locale based on i18next language. Returns undefined for English (date-fns default). */
function getDateLocale(): Locale | undefined {
  return DATE_FNS_LOCALES[i18next.language];
}

/** Safely format a date with date-fns format(). Returns fallback on any error. */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  fmt: string,
  fallback = '',
): string {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return format(d, fmt, { locale: getDateLocale() });
  } catch {
    return fallback;
  }
}

/** Safely call toLocaleDateString(). Returns fallback on any error. */
export function safeToLocaleDate(
  value: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = '',
): string {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString(document.documentElement.lang || undefined, options);
  } catch {
    return fallback;
  }
}

/** Safely call toLocaleString(). Returns fallback on any error. */
export function safeToLocaleString(
  value: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = '',
): string {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return d.toLocaleString(document.documentElement.lang || undefined, options);
  } catch {
    return fallback;
  }
}

/** Safely call toLocaleTimeString(). Returns fallback on any error. */
export function safeToLocaleTime(
  value: string | number | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback: string | null = null,
): string | null {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return d.toLocaleTimeString(document.documentElement.lang || undefined, options);
  } catch {
    return fallback;
  }
}

/** Safely call formatDistanceToNow(). Returns fallback on any error. */
export function safeDistanceToNow(
  value: string | number | Date | null | undefined,
  options?: FormatDistanceToNowOptions,
  fallback = '',
): string {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return formatDistanceToNow(d, { locale: getDateLocale(), ...options });
  } catch {
    return fallback;
  }
}

/** Safely call formatDistanceToNowStrict(). Returns fallback on any error. */
export function safeDistanceToNowStrict(
  value: string | number | Date | null | undefined,
  options?: FormatDistanceToNowStrictOptions,
  fallback = '',
): string {
  if (value == null || value === '') return fallback;
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fallback;
    return formatDistanceToNowStrict(d, { locale: getDateLocale(), ...options });
  } catch {
    return fallback;
  }
}

/** Safely parse JSON. Returns fallback on SyntaxError. */
export function safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
  if (value == null || value === '') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
